import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useCreateRelease } from '@/hooks/useLibraryReleases';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const BRAND_OPTIONS = [
  { value: 'saint_gobain', label: 'Saint-Gobain' },
  { value: 'knauf', label: 'Knauf' },
  { value: 'fassa', label: 'Fassa Bortolo' },
];

const CONTENT_OPTIONS = [
  { value: 'materials', label: 'Listino (materiali)' },
  { value: 'stratigraphies', label: 'Stratigrafie' },
  { value: 'bundle', label: 'Bundle (listino + strat)' },
];

export default function NewReleaseDialog({ open, onOpenChange }: Props) {
  const [brand, setBrand] = useState('fassa');
  const [contentType, setContentType] = useState<'materials' | 'stratigraphies' | 'bundle'>('stratigraphies');
  const [year, setYear] = useState(new Date().getFullYear());
  const [suffix, setSuffix] = useState('');
  const [name, setName] = useState('');
  const [price, setPrice] = useState(99);
  const create = useCreateRelease();
  const navigate = useNavigate();

  const contentLabel = contentType === 'materials' ? 'Listino' : contentType === 'stratigraphies' ? 'Stratigrafie' : 'Bundle';
  const brandLabel = BRAND_OPTIONS.find((b) => b.value === brand)?.label ?? brand;
  const slug = `${contentType === 'bundle' ? 'bundle' : 'addon'}_${brand}_${contentType}_${year}${suffix.replace(/[^\w]/g, '_')}`;
  // Il nome admin include anche l'anno (aiuta a riconoscere le release nei filtri/dropdown).
  // Lo Store cliente toglie l'anno dal nome mostrato (cleanName in LibraryCard).
  const defaultName = name || `${contentLabel} ${brandLabel} ${year}${suffix}`;

  const handleCreate = async () => {
    const id = await create.mutateAsync({
      slug,
      name: defaultName,
      brand_slug: brand,
      content_type: contentType,
      version_year: year,
      version_suffix: suffix || undefined,
      price_eur: price,
    });
    onOpenChange(false);
    setName('');
    setSuffix('');
    navigate(`/admin/releases/${id}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nuova release</DialogTitle>
          <DialogDescription>
            Crea un pacchetto vendibile nello Store. Dopo potrai aggiungerci materiali e stratigrafie dal Catalogo.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Brand</Label>
              <Select value={brand} onValueChange={setBrand}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{BRAND_OPTIONS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo contenuto</Label>
              <Select value={contentType} onValueChange={(v) => setContentType(v as typeof contentType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CONTENT_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Anno</Label>
              <Input type="number" min={2024} max={2099} value={year} onChange={(e) => setYear(Number(e.target.value))} />
            </div>
            <div>
              <Label>Suffisso</Label>
              <Input placeholder=".05" value={suffix} onChange={(e) => setSuffix(e.target.value)} />
            </div>
            <div>
              <Label>Prezzo €</Label>
              <Input type="number" min={0} value={price} onChange={(e) => setPrice(Number(e.target.value))} />
            </div>
          </div>
          <div>
            <Label>Nome release</Label>
            <Input placeholder={defaultName} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="text-xs text-zinc-500 bg-zinc-50 rounded p-2">
            <strong>Slug auto:</strong> <code className="text-[10px]">{slug}</code>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={handleCreate} disabled={create.isPending}>
            {create.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Crea
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
